import type { PotDetailsV2Response } from '@billda/api';
import type { SitePlot } from '@billda/design-system';

/**
 * Pot breakdown adapter: one flat API response in, one flat row list out.
 *
 * The response carries a flat `allocations[]` plus a flat `deductions[]` and
 * the ledger keeps them flat. A pot is already one gang for one week, so the
 * screen has spent its grouping dimension; banding by site and plot on top of
 * that cost four headings for five rows, and gave a plot-scoped deduction its
 * own heading with a negative total, which read as a plot that lost money.
 * Where a row happened is a column now, and that column is the only thing
 * saying whether a deduction is attached to a plot, to a site, or to neither.
 */

type PotAllocation = PotDetailsV2Response['allocations'][number];
type PotDeduction = PotDetailsV2Response['deductions'][number];

interface PotLedgerEntryBase {
  /** Stable react key. */
  key: string;
  /**
   * Raw status string off the wire. Kept unmapped: `statusToken` degrades an
   * unknown status gracefully, and `data-status` is the e2e contract, so
   * folding one status into another here would lie twice.
   */
  status: string;
  reference: string | null;
  /**
   * Null when the viewer has no financial permission — the API nulls the
   * amount rather than sending zero, and the row renders it as withheld.
   */
  amount: number | null;
  /** ISO timestamp. */
  when: string;
  who: string | null;
  siteId: number | null;
  siteName: string | null;
  plotId: number | null;
  plotReference: string | null;
  /**
   * What the Site / Plot cell renders. Null on a row scoped to neither, and
   * site-only on a deduction attached to a site rather than to one of its
   * plots.
   */
  sitePlot: SitePlot | null;
}

interface WorkApproval {
  approvedByName: string | null;
  approvedAt: string | null;
  rejectedByName: string | null;
  rejectionReason: string | null;
}

export interface PotLabourEntry extends PotLedgerEntryBase, WorkApproval {
  kind: 'labour';
  allocationId: string;
  title: string;
  labourItemId: number | null;
  pricingMode: 'FixedAmount' | 'UnitRate' | null;
  quantityClaimed: number | null;
  unitRate: number | null;
  unitLabel: string | null;
}

export interface PotVariationEntry extends PotLedgerEntryBase, WorkApproval {
  kind: 'variation';
  allocationId: string;
  variationRequestId: string | null;
  /** Work type display name, or the free text when the DTO has no type. */
  title: string;
  /** Null once the free text has been promoted to the title. */
  description: string | null;
}

export interface PotDeductionEntry extends PotLedgerEntryBase {
  kind: 'deduction';
  deductionId: string;
  title: string;
  reviewedByName: string | null;
  reviewedAt: string | null;
}

export type PotLedgerEntry =
  | PotLabourEntry
  | PotVariationEntry
  | PotDeductionEntry;

export interface PotLedgerSource {
  allocations: readonly PotAllocation[];
  deductions: readonly PotDeduction[];
  search?: string;
}

function clean(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/** No site means no cell: an org-level deduction belongs to nowhere on site. */
function toSitePlot(
  siteName: string | null,
  plotReference: string | null
): SitePlot | null {
  const site = clean(siteName);
  if (site.length === 0) {
    return null;
  }
  const plot = clean(plotReference);
  return { site, plot: plot.length > 0 ? plot : null };
}

function toWorkEntry(
  allocation: PotAllocation
): PotLabourEntry | PotVariationEntry {
  const base = {
    key: allocation.id,
    allocationId: allocation.id,
    status: allocation.status,
    reference: allocation.uniqueReference,
    amount: allocation.amount,
    when: allocation.submittedAt,
    who: allocation.bookedByName,
    siteId: allocation.siteId,
    siteName: allocation.siteName,
    plotId: allocation.plotId,
    plotReference: allocation.plotReference,
    sitePlot: toSitePlot(allocation.siteName, allocation.plotReference),
    approvedByName: allocation.approvedByName,
    approvedAt: allocation.approvedAt,
    rejectedByName: allocation.rejectedByName,
    rejectionReason: allocation.rejectionReason,
  };

  if (!allocation.isVariation) {
    return {
      ...base,
      kind: 'labour',
      title: allocation.itemName,
      labourItemId: allocation.labourItemId,
      pricingMode: allocation.pricingMode,
      quantityClaimed: allocation.quantityClaimed,
      unitRate: allocation.unitRate,
      unitLabel: allocation.unitLabel,
    };
  }

  // `workTypeName` is nullable on this DTO, unlike the site screen's, so the
  // free-text entry has to be able to stand in as the title.
  return {
    ...base,
    kind: 'variation',
    variationRequestId: allocation.variationRequestId,
    title: allocation.workTypeName ?? allocation.itemName,
    description: allocation.workTypeName ? allocation.itemName : null,
  };
}

function toDeductionEntry(deduction: PotDeduction): PotDeductionEntry {
  return {
    kind: 'deduction',
    key: deduction.id,
    deductionId: deduction.id,
    title: deduction.description,
    status: deduction.status,
    reference: deduction.uniqueReference,
    // Deductions take money out of the pot, so they carry a negative amount.
    amount: deduction.amount == null ? null : -deduction.amount,
    when: deduction.createdAt,
    who: deduction.createdByName,
    siteId: deduction.siteId,
    siteName: deduction.siteName,
    plotId: deduction.plotId,
    plotReference: deduction.plotReference,
    sitePlot: toSitePlot(deduction.siteName, deduction.plotReference),
    reviewedByName: deduction.reviewedByName,
    reviewedAt: deduction.reviewedAt,
  };
}

/**
 * Rows naming nothing sort after rows that do, so a site-wide deduction
 * follows that site's plots and an org-wide one ends the list. Plot numbers
 * compare numerically: "9" before "10".
 */
function compareLabel(a: string, b: string): number {
  if (a.length === 0 || b.length === 0) {
    if (a.length === b.length) {
      return 0;
    }
    return a.length === 0 ? 1 : -1;
  }
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function compareRows(a: PotLedgerEntry, b: PotLedgerEntry): number {
  const bySite = compareLabel(clean(a.siteName), clean(b.siteName));
  if (bySite !== 0) {
    return bySite;
  }
  const byPlot = compareLabel(clean(a.plotReference), clean(b.plotReference));
  if (byPlot !== 0) {
    return byPlot;
  }
  return clean(a.when).localeCompare(clean(b.when));
}

function matchesText(value: string | null | undefined, query: string): boolean {
  return (value ?? '').toLowerCase().includes(query);
}

/**
 * Search covers what the row shows, site and plot included — with the bands
 * gone, that text only exists on the rows themselves.
 */
function matchesEntry(entry: PotLedgerEntry, query: string): boolean {
  if (
    matchesText(entry.title, query) ||
    matchesText(entry.reference, query) ||
    matchesText(entry.siteName, query) ||
    matchesText(entry.plotReference, query)
  ) {
    return true;
  }
  return entry.kind === 'variation' && matchesText(entry.description, query);
}

/**
 * Rows in one list, ordered by site, then plot, then when they were booked.
 *
 * No totals of any kind are computed here. The pot's gross, costs and net are
 * the Overview tab's job and the backend's numbers, so the breakdown does no
 * money arithmetic at all — which retires the G5 client-sum caveat on this
 * screen rather than leaving it open.
 */
export function toPotLedgerRows({
  allocations,
  deductions,
  search = '',
}: PotLedgerSource): PotLedgerEntry[] {
  const entries: PotLedgerEntry[] = [
    ...allocations.map(toWorkEntry),
    ...deductions.map(toDeductionEntry),
  ];
  const query = search.trim().toLowerCase();
  const matched =
    query.length === 0
      ? entries
      : entries.filter(entry => matchesEntry(entry, query));
  return matched.sort(compareRows);
}
